/**
 * ReferenceValueSelect — the one dropdown implementation for the four
 * "simple" reference-data categories (season, competition_stage,
 * match_type, venue_surface). player_position is NOT rendered through this
 * component — it needs optgroups by tactical position group, handled by its
 * own call sites via `useReferenceValues("player_position", ...)` directly.
 *
 * Always fetches with `includeInactive: true` so a record's current value
 * is never silently dropped from the list, even if it has since been
 * deactivated (or isn't a recognized reference code at all — e.g. a legacy
 * free-text value stored before this dropdown existed). Deprecated/unknown
 * values are appended, suffixed, and are the ONLY way they can appear — a
 * value not currently selected and not active is never offered as a new
 * choice.
 *
 * PMA-UI wave: built on the `Select` primitive (`components/ui/select.tsx`,
 * Radix-based) instead of a native `<select>`. Public props and observable
 * behavior are unchanged from the PMA-REF version — see
 * `ReferenceValueSelect.test.tsx`. `competition_stage` (31 values) gets a
 * filter box via `useSelectFilter` automatically once a category's option
 * count crosses `SEARCH_THRESHOLD` — directly fulfils the plan's "31
 * competition stages... unusable as a plain list" requirement, and adapts
 * on its own if a small category grows past the threshold without needing
 * a category-name special case.
 */

import { useTranslation } from "react-i18next";
import {
  useReferenceValues,
  withCurrentValuePreserved,
} from "../hooks/useReferenceValues";
import type { ReferenceCategory } from "../lib/referenceDataApi";
import {
  Select,
  SelectContent,
  SelectEmpty,
  SelectItem,
  SelectSearchInput,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useSelectFilter } from "./ui/useSelectFilter";

interface ReferenceValueSelectProps {
  category: ReferenceCategory;
  value: string;
  onChange: (code: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  "data-testid"?: string;
}

/** Radix `Select.Item` forbids an empty-string value (it's reserved
 *  internally for "nothing selected"). The placeholder option needs a real
 *  sentinel value instead — mapped back to "" at the public prop boundary
 *  so callers never see it. */
const UNSET = "__unset__";

/** Below this many options, a filter box is more friction than it saves
 *  (season/match_type/venue_surface: 2-5 values each). At or above it
 *  (competition_stage: 31), it's the difference between usable and not. */
const SEARCH_THRESHOLD = 8;

export default function ReferenceValueSelect({
  category,
  value,
  onChange,
  id,
  required,
  disabled,
  className,
  placeholder,
  "data-testid": dataTestId,
}: ReferenceValueSelectProps) {
  const { t, i18n } = useTranslation("admin");
  const { data, isLoading } = useReferenceValues(category, {
    includeInactive: true,
  });

  const options = withCurrentValuePreserved(data, value, i18n.language);

  // Flattened to {value, label} once — feeds both the trigger's explicit
  // label (see below) and `useSelectFilter`, so there's a single source for
  // "what does this option display as" instead of recomputing the
  // deprecated-suffix text in two places.
  const displayOptions = options.map((opt) => ({
    value: opt.code,
    label: opt.isDeprecated
      ? t("referenceData.deprecatedOption", {
          label: opt.label,
          defaultValue: `${opt.label} (deprecated)`,
        })
      : opt.label,
    isDeprecated: opt.isDeprecated,
  }));

  // Passed explicitly rather than relying on Radix's own selected-item-text
  // mirroring, which only knows a label once its SelectItem has mounted
  // (i.e. after the popup has been opened at least once) — this component
  // must show the correct label on first render too.
  const selectedLabel = displayOptions.find(
    (opt) => opt.value === value,
  )?.label;

  const { query, setQuery, visible } = useSelectFilter(displayOptions);
  const searchable = displayOptions.length > SEARCH_THRESHOLD;
  const itemsToRender = searchable ? visible : displayOptions;

  return (
    <Select
      value={value === "" ? UNSET : value}
      onValueChange={(next) => onChange(next === UNSET ? "" : next)}
      disabled={disabled || isLoading}
      required={required}
    >
      <SelectTrigger id={id} className={className} data-testid={dataTestId}>
        <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {placeholder !== undefined && (
          <SelectItem value={UNSET}>{placeholder}</SelectItem>
        )}
        {searchable && (
          <SelectSearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
          />
        )}
        {itemsToRender.length === 0 && (
          <SelectEmpty>{t("referenceData.empty")}</SelectEmpty>
        )}
        {itemsToRender.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            deprecated={opt.isDeprecated}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
